import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import useTableConfiguration from "../hooks/useTableConfiguration";
import { svCompare } from '../utils/localeFormatter';
import {
  FileText,
  Download,
  Share2,
  ChevronUp,
  ChevronDown,
  Star,
  Eye,
} from "lucide-react";

const TableComponent = ({
  data,
  loading,
  filters,
  onViewDocument,
  onDownloadDocument,
  onShareDocument,
  onBookmarkDocument,
  onViewPdf,
  bookmarkedIds = [],
  activeTab,
  getTableHeaders: getTableHeadersProp,
  getConfigurationForReportType: getConfigurationForReportTypeProp,
}) => {
  const defaultTableConfig = useTableConfiguration();
  const getTableHeaders = getTableHeadersProp || defaultTableConfig.getTableHeaders;
  const getConfigurationForReportType =
    getConfigurationForReportTypeProp || defaultTableConfig.getConfigurationForReportType;

  // State for sorting and column widths
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const tableRef = useRef(null);
  const startXRef = useRef(null);
  const startWidthRef = useRef(null);

  // Get headers from configuration - this will now handle all 4 report types correctly
  const headers = useMemo(() => {
    const configHeaders = getTableHeaders(activeTab);
    return configHeaders;
  }, [activeTab, getTableHeaders]);

  // Get current configuration for custom fields
  const currentConfig = useMemo(() => {
    const config = getConfigurationForReportType(activeTab);
    return config;
  }, [activeTab, getConfigurationForReportType]);

  // Process data with custom fields
  const processedData = useMemo(() => {
    if (
      !currentConfig ||
      !currentConfig.customFields ||
      currentConfig.customFields.length === 0
    ) {
      return data;
    }

    return data.map((row) => {
      const processedRow = { ...row };


      return processedRow;
    });
  }, [data, currentConfig]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return processedData;

    return [...processedData].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      // Handle different data types
      if (typeof aValue === "string" && typeof bValue === "string") {
        const result = svCompare(aValue, bValue);
        return sortConfig.direction === "asc" ? result : -result;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [processedData, sortConfig]);

  const formatCellValue = (value, key) => {
    // Check if this is a custom field by looking at the current config
    const isCustomField = currentConfig?.customFields?.some(f => f.id === key);
    

    
    if (!value || value === '(Empty/Not Specified)' || value === null || value === undefined) {
      return 'N/A';
    }
    
    // Special handling for date fields - format as DD-MM-YYYY
    if (key === 'date_of_visit') {
      try {
        // Handle various date formats
        let dateObj;
        
        if (value instanceof Date) {
          dateObj = value;
        } else if (typeof value === 'string') {
          // Handle different string formats
          if (value.includes('-')) {
            // YYYY-MM-DD or DD-MM-YYYY format
            const parts = value.split('-');
            if (parts[0].length === 4) {
              // YYYY-MM-DD format
              dateObj = new Date(value);
            } else {
              // DD-MM-YYYY format - already correct, just return
              return value;
            }
          } else if (value.includes('/')) {
            // Handle MM/DD/YYYY or DD/MM/YYYY
            dateObj = new Date(value);
          } else {
            // Try to parse as-is
            dateObj = new Date(value);
          }
        }
        
        if (dateObj && !isNaN(dateObj.getTime())) {
          // Format as DD-MM-YYYY
          const day = dateObj.getDate().toString().padStart(2, '0');
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
          const year = dateObj.getFullYear();
          return `${day}-${month}-${year}`;
        }
      } catch (error) {
        console.log('Date formatting error for value:', value, error);
      }
      
      // If date parsing fails, return original value
      return String(value);
    }
    
    const stringValue = String(value);
    
    // For custom fields, show more content
    if (isCustomField) {
      const maxLength = 60; // Longer for custom fields
      if (stringValue.length <= maxLength) {
        return stringValue;
      }
      return stringValue.substring(0, maxLength) + '...';
    }
    
    // Existing responsive logic for standard fields...
    const getResponsiveMaxLength = (columnKey) => {
      const columnLengths = {
        sm: {
          customer_name: 15,
          site_name: 12,
          description: 20,
          failure_reason: 15,
          part_number: 12,
          serial_number: 12,
          default: 15
        },
        md: {
          customer_name: 25,
          site_name: 20,
          description: 35,
          failure_reason: 25,
          part_number: 15,
          serial_number: 15,
          default: 25
        },
        lg: {
          customer_name: 50,
          site_name: 40,
          description: 80,
          failure_reason: 50,
          part_number: 25,
          serial_number: 25,
          default: 50
        }
      };

      const width = window.innerWidth;
      let sizeKey = 'sm';
      if (width >= 1024) sizeKey = 'lg';
      else if (width >= 768) sizeKey = 'md';

      return columnLengths[sizeKey][columnKey] || columnLengths[sizeKey].default;
    };

    const maxLength = getResponsiveMaxLength(key);
    
    if (stringValue.length <= maxLength) {
      return stringValue;
    }
    
    return stringValue.substring(0, maxLength) + '...';
  };
  // Column resizing logic
  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing || !resizingColumn) return;

      const diffX = e.clientX - startXRef.current;
      const newWidth = Math.max(50, startWidthRef.current + diffX); // Minimum 50px

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    },
    [isResizing, resizingColumn]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();

    // Get current width from DOM
    const headerElement = e.target.closest("th");
    if (!headerElement) return;

    setIsResizing(true);
    setResizingColumn(columnKey);
    startXRef.current = e.clientX;
    startWidthRef.current = headerElement.offsetWidth;


  }, []);

  // Get column style with proper width handling
  const getColumnStyle = (header) => {
    // First check if user has manually resized this column
    const userWidth = columnWidths[header.key];
    if (userWidth) {
      return {
        width: `${userWidth}px`,
        minWidth: `${userWidth}px`,
        maxWidth: `${userWidth}px`, // Added maxWidth to prevent expansion
      };
    }

    // Use configuration width as fallback
    const widthFromConfig =
      currentConfig?.fields?.find((f) => f.key === header.key)?.width ||
      currentConfig?.customFields?.find((f) => f.id === header.key)?.width;
    if (widthFromConfig) {
      return {
        width: `${widthFromConfig}px`,
        minWidth: `${widthFromConfig}px`,
        maxWidth: `${widthFromConfig}px`,
      };
    }

    // Default widths based on column type - increased for better horizontal scrolling
    const defaultWidths = {
      date_of_visit: 150,
      customer_name: 250, // Increased
      country_name: 130,
      site_name: 220, // Increased
      description: 300, // Increased
      serial_number: 160,
      failure_reason: 200, // Increased
      part_number: 150,
      rig_model: 150,
      rig_id: 110,
      actions: 180,
    };

    const defaultWidth = defaultWidths[header.key] || 180; // Increased default
    return {
      width: `${defaultWidth}px`,
      minWidth: `${defaultWidth}px`,
      maxWidth: `${defaultWidth}px`,
    };
  };

  // Calculate minimum table width to force horizontal scrolling when needed
  const calculateMinTableWidth = () => {
    let totalWidth = 0;
    headers.forEach((header) => {
      const userWidth = columnWidths[header.key];
      if (userWidth) {
        totalWidth += userWidth;
      } else {
        const widthFromConfig =
          currentConfig?.fields?.find((f) => f.key === header.key)?.width ||
          currentConfig?.customFields?.find((f) => f.id === header.key)?.width;
        if (widthFromConfig) {
          totalWidth += widthFromConfig;
        } else {
          const defaultWidths = {
            date_of_visit: 150,
            customer_name: 250,
            country_name: 130,
            site_name: 220,
            description: 300,
            serial_number: 160,
            failure_reason: 200,
            part_number: 150,
            rig_model: 150,
            rig_id: 110,
            actions: 180,
          };
          totalWidth += defaultWidths[header.key] || 180;
        }
      }
    });
    return Math.max(totalWidth, 1200); // Minimum 1200px to ensure horizontal scroll
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-b-2 border-electric-blue mx-auto mb-4"></div>
          <p className="text-gray-500">Loading data...</p>
        </div>
      </div>
    );
  }

  if (sortedData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-n5 mb-4" />
          <p className="text-gray-500 text-lg mb-2">
            No data found
          </p>
          <p className="text-n5">
            Try adjusting your filters or search terms.
          </p>
        </div>
      </div>
    );
  }

  const minTableWidth = calculateMinTableWidth();

  return (
    <div className="h-full bg-white">
      {/* Table container with both horizontal and vertical scrolling */}
      <div
        className="h-full overflow-auto"
        ref={tableRef}
        style={{
          // Ensure proper scrolling behavior
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <table
          className="divide-y divide-gray-200"
          style={{
            tableLayout: "fixed",
            minWidth: `${minTableWidth}px`, // Force minimum width for horizontal scrolling
            width: "100%",
          }}
        >
          {/* Enhanced Header with working resizable columns */}
          <thead className="bg-n2 sticky top-0 z-10 border-b border-n3">
            <tr>
              {headers.map((header) => (
                <th
                  key={header.key}
                  className={`px-3 py-3 text-left text-xs text-sandvik-black tracking-wider border-r border-n3 last:border-r-0 relative ${
                    header.key !== "actions"
                      ? "cursor-pointer hover:bg-n3 transition-colors select-none"
                      : ""
                  }`}
                  style={getColumnStyle(header)}
                  onClick={(e) => {
                    // Only sort if not clicking on resize handle
                    if (
                      !e.target.classList.contains("resize-handle") &&
                      !e.target.parentElement?.classList.contains(
                        "resize-handle"
                      ) &&
                      header.key !== "actions"
                    ) {
                      handleSort(header.key);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1 flex-1 min-w-0 pr-2">
                      <span className="truncate">{header.label}</span>
                      {header.key !== "actions" &&
                        sortConfig.key === header.key && (
                          <span className="text-white flex-shrink-0">
                            {sortConfig.direction === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </span>
                        )}
                    </div>

                    {/* Fixed resize handle */}
                    {header.key !== "actions" && (
                      <div
                        className="resize-handle absolute right-0 top-0 bottom-0 w-3 cursor-col-resize hover:bg-white hover:bg-opacity-30 transition-colors flex items-center justify-center"
                        onMouseDown={(e) => handleMouseDown(e, header.key)}
                        title="Drag to resize column"
                        style={{ zIndex: 10 }}
                      >
                        <div className="w-0.5 h-4 bg-gray-400 opacity-50"></div>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-gray-100 bg-white">
            {sortedData.map((item, index) => {
              const rowId = item.report_id || index;
              const uniqueKey = item.report_id
                ? `${item.report_id}-${index}`
                : `row-${index}`;
              return (
                <tr
                  key={uniqueKey}
                  className={`${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-n2 transition-colors duration-150 ease-in-out border-b border-n3`}
                >
                  {headers.map((header) => {
                    if (header.key === "actions") {
                      return (
                        <td
                          key={header.key}
                          className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 border-r border-gray-100 last:border-r-0"
                          style={getColumnStyle(header)}
                        >
                          <div className="flex space-x-1">
                            <button
                              onClick={() => onViewDocument(item.report_id)}
                              className="text-gray-600 hover:text-black p-1 hover:bg-gray-100 transition-colors duration-150"
                              title="View document"
                            >
                              <FileText className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => onDownloadDocument(item.report_id)}
                              className="text-gray-600 hover:text-black p-1 hover:bg-gray-100 transition-colors duration-150"
                              title="Download document"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => onShareDocument(item.report_id)}
                              className="text-gray-600 hover:text-black p-1 hover:bg-gray-100 transition-colors duration-150"
                              title="Share document"
                            >
                              <Share2 className="h-3 w-3" />
                            </button>
                            {onViewPdf && (
                              <button
                                onClick={() => onViewPdf(item.report_id)}
                                className="text-gray-600 hover:text-black p-1 hover:bg-gray-100 transition-colors duration-150"
                                title="View PDF"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            {onBookmarkDocument && (
                              <button
                                onClick={() => onBookmarkDocument(item.report_id)}
                                className={`p-1 transition-colors duration-150 ${
                                  bookmarkedIds.includes(item.report_id)
                                    ? "text-gold hover:text-gold-g3"
                                    : "text-gray-600 hover:text-gold hover:bg-gray-100"
                                }`}
                                title={bookmarkedIds.includes(item.report_id) ? "Remove bookmark" : "Bookmark"}
                              >
                                <Star className={`h-3 w-3 ${bookmarkedIds.includes(item.report_id) ? "fill-current" : ""}`} />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    }

                    const cellValue = item[header.key];
                    const formattedValue = formatCellValue(
                      cellValue,
                      header.key
                    );

                    // Debug log for custom fields
                    const isCustomField = currentConfig?.customFields?.some(
                      (f) => f.id === header.key
                    );
                   

                    return (
                      <td
                        key={header.key}
                        className="px-3 py-3 text-sm text-gray-900 border-r border-gray-100 last:border-r-0 text-left"
                        style={getColumnStyle(header)}
                      >
                        <div
                          title={cellValue} // Full content on hover
                          className=" text-left"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formattedValue}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Resizing indicator */}
      {isResizing && (
        <div
          className="fixed inset-0 cursor-col-resize bg-black bg-opacity-10 select-none"
          style={{ zIndex: 9999 }}
        />
      )}
    </div>
  );
};

export default TableComponent;
